import { type FC, useState } from "react";
import { Box, AppBar, TablePagination, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { usePlatformPagesQuery } from "@/partner/entities/platformPages";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { PlatformPagesListItem } from "./PlatformPagesListItem";
import { PlatformPagesListToolbar } from "./PlatformPagesListToolbar";

interface PlatformPagesListProps {
  onEditPage: (id: string) => void;
}

export const PlatformPagesList: FC<PlatformPagesListProps> = ({
  onEditPage,
}) => {
  const { t } = useTranslation();
  const {
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
    filter: { search },
  } = useFilter();

  const { data, loading } = usePlatformPagesQuery({
    variables: {
      page,
      rowsPerPage,
      filter: {
        search,
      },
    },
  });

  const pages = data?.platformPages?.nodes || [];
  const count = data?.platformPages?.count || 0;

  const [selectedItems, setSelectedItems] = useState<{ id: string }[]>([]);
  const selectedIds = selectedItems.map((item) => item.id);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedItems(pages.map((p) => ({ id: p.id })));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectOne = (
    id: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    event.stopPropagation();
    if (selectedIds.includes(id)) {
      setSelectedItems(selectedItems.filter((item) => item.id !== id));
    } else {
      setSelectedItems([...selectedItems, { id }]);
    }
  };

  const numSelected = selectedIds.length;
  const indeterminate = numSelected > 0 && numSelected < pages.length;

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (pages.length === 0) {
    return <EmptyListCard title={t("platformPages.list.empty")} />;
  }

  return (
    <Box>
      <AppBar position="static" color="default" elevation={0}>
        <PlatformPagesListToolbar
          indeterminate={indeterminate}
          numSelected={numSelected}
          nodes={pages}
          onSelectAllClick={handleSelectAll}
          selectedItems={selectedItems}
          onCloseSelected={() => setSelectedItems([])}
        />
      </AppBar>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
        {pages.map((pageItem) => (
          <PlatformPagesListItem
            key={pageItem.id}
            page={pageItem}
            selected={selectedIds.includes(pageItem.id)}
            onSelectOneChange={handleSelectOne}
            onClickItem={onEditPage}
          />
        ))}
      </Box>

      {/* Пагинация */}
      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Box>
  );
};
